// Anmeldezustand der Oberfläche: wer ist angemeldet, anmelden, abmelden.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ABGEMELDET_EREIGNIS, ApiFehler, authApi, type LoginEingabe, type Nutzer } from "../api";
import { echtzeit } from "../api/echtzeit";
import { MeldungenProvider } from "../components/Meldungen";
import { entwuerfeLeeren } from "../daten/entwuerfe";
import { listeSetzen } from "../daten/liste";
import { nutzerGewechselt } from "../daten/sitzung";
import { ZweiFaktorPflicht } from "../nutzer/ZweiFaktorPflicht";
import { ROLLEN } from "../pages/einstellungen/rollen";

/** Anmeldung, bei Zwei-Faktor mit dem Code aus der App (oder einem Wiederherstellungscode) */
export type Anmeldung = LoginEingabe;

/** Zwei-Faktor-Stand aus `GET /api/auth/me` (Runde 8, Agent 8C): `einrichten_noetig` = die Organisation verlangt es, der Nutzer hat es nicht */
function einrichtenNoetig(n: Nutzer | null): boolean {
  return n?.zwei_faktor?.einrichten_noetig === true;
}

interface AuthWert {
  nutzer: Nutzer | null;
  laedt: boolean;
  anmelden: (eingabe: Anmeldung) => Promise<Nutzer>;
  abmelden: () => Promise<void>;
  neuLaden: () => Promise<void>;
}

const AuthKontext = createContext<AuthWert | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [nutzer, setNutzerState] = useState<Nutzer | null>(null);
  const [laedt, setLaedt] = useState(true);
  const aktuell = useRef<string | null>(null);
  const zuletzt = useRef<string | null>(null);

  // Jeder Wechsel der angemeldeten Person (auch Abmelden) leert Smart Views, Pins, Trefferzahlen und Vorgeladenes
  // synchron, bevor die Oberfläche mit dem neuen Nutzer zeichnet. Meldet sich jemand anderes an als zuletzt,
  // gehen auch Entwürfe und die gemerkte Liste weg (bei abgelaufener Sitzung und gleichem Nutzer bleiben sie).
  const setNutzer = useCallback((u: Nutzer | null) => {
    // Eigene Rollen (Runde 8, Agent 8C): ihren Namen für die Anzeige in `ROLLEN` (pages/einstellungen/rollen.ts) nachtragen
    const rolleName = u?.role_name;
    if (u && rolleName && !(u.role in ROLLEN)) ROLLEN[u.role] = rolleName;
    const id = u?.id ?? null;
    if (id !== aktuell.current) {
      nutzerGewechselt();
      if (id && zuletzt.current && id !== zuletzt.current) {
        entwuerfeLeeren();
        listeSetzen(null);
      }
    }
    aktuell.current = id;
    if (id) zuletzt.current = id;
    setNutzerState(u);
  }, []);

  const neuLaden = useCallback(async () => {
    try {
      const { user } = await authApi.me();
      setNutzer(user);
    } catch (e) {
      if (e instanceof ApiFehler && e.status === 401) setNutzer(null);
      else throw e;
    } finally {
      setLaedt(false);
    }
  }, [setNutzer]);

  useEffect(() => {
    neuLaden().catch(() => setLaedt(false));
    const weg = () => setNutzer(null);
    window.addEventListener(ABGEMELDET_EREIGNIS, weg);
    return () => window.removeEventListener(ABGEMELDET_EREIGNIS, weg);
  }, [neuLaden, setNutzer]);

  useEffect(() => {
    if (nutzer) echtzeit.starten();
    else echtzeit.stoppen();
  }, [nutzer]);

  // Rolle, Deaktivierung oder Zwei-Faktor des eigenen Kontos wurde (von jemand anderem) geändert: Rechte neu laden
  useEffect(() => {
    if (!nutzer) return;
    return echtzeit.abonnieren((e) => {
      if (e.type === "user.updated" && (e.data as { user_id?: string } | undefined)?.user_id === nutzer.id) void neuLaden().catch(() => undefined);
    });
  }, [nutzer, neuLaden]);

  const anmelden = useCallback(async (eingabe: Anmeldung) => {
    const { user } = await authApi.login(eingabe);
    setNutzer(user);
    return user;
  }, [setNutzer]);

  const abmelden = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      // Ausdrücklich abgemeldet: Entwürfe und gemerkte Liste dieses Tabs gehören niemandem mehr.
      entwuerfeLeeren();
      listeSetzen(null);
      setNutzer(null);
    }
  }, [setNutzer]);

  const wert = useMemo(() => ({ nutzer, laedt, anmelden, abmelden, neuLaden }), [nutzer, laedt, anmelden, abmelden, neuLaden]);
  // Verlangt die Organisation Zwei-Faktor und der Nutzer hat es nicht, kommt er erst nach der Einrichtung in die App
  return (
    <AuthKontext.Provider value={wert}>
      {einrichtenNoetig(nutzer) ? (
        <MeldungenProvider>
          <ZweiFaktorPflicht />
        </MeldungenProvider>
      ) : (
        children
      )}
    </AuthKontext.Provider>
  );
}

export function useAuth(): AuthWert {
  const wert = useContext(AuthKontext);
  if (!wert) throw new Error("useAuth außerhalb von AuthProvider");
  return wert;
}

/** Angemeldeter Nutzer (nur innerhalb geschützter Seiten verwenden). */
export function useNutzer(): Nutzer {
  const { nutzer } = useAuth();
  if (!nutzer) throw new Error("Nicht angemeldet");
  return nutzer;
}
